import * as Sentry from "@sentry/react";
import { collection, getDocs } from "firebase/firestore";
import { Formik, useField } from "formik";
import { Eye, UserPlus } from "lucide-react";
import React, { useEffect, useId, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, SectionHeader } from "@/components/ui/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";

import { db } from "../../../api/firebaseApi";
import {
  getFirebaseUsers,
  getLoginAsToken,
  updateFirebaseUser
} from "../../../api/OpenAIApi";
import { validatePassword } from "../../../helpers/string_helpers";
import { DATABASE } from "../../../types/tables-data";

const SUBSCRIPTION_TYPES = ["free_trial", "paid"];
const LAWYER_ACCOUNT_TYPES = ["standard", "full"];
const VISA_TYPES = ["EB-2 NIW", "O-1 A"];

interface Customer {
  id: string;
  email: string;
  practiceName: string;
}

interface UserFormState {
  email: string;
  firstName: string;
  lastName: string;
  disabled: boolean;
  newPassword: string;
  role: string;
  practiceName: string;
  visaType: string;
  subscriptionStatus: string;
  lawyerRole: string;
  customerId: string;
}

const validationSchema = Yup.object({
  email: Yup.string().email("Invalid email address").required("Required"),
  firstName: Yup.string().required("Required"),
  lastName: Yup.string().required("Required"),
  newPassword: Yup.string().test(
    "password-validation",
    "Password does not meet all requirements",
    (value) => {
      if (!value) return true; // Allow empty password (optional field)
      return validatePassword(value).isValid;
    }
  )
});

const humanize = (value: string) =>
  value
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const TextField = ({
  name,
  label,
  type = "text",
  required = false,
  readOnly = false
}: {
  name: keyof UserFormState;
  label: string;
  type?: string;
  required?: boolean;
  readOnly?: boolean;
}) => {
  const id = useId();
  const [field, meta] = useField<string>(name);
  const error = meta.touched ? meta.error : undefined;
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>
        {label}
        {required ? <span className="text-danger">*</span> : null}
      </Label>
      <Input
        id={id}
        type={type}
        readOnly={readOnly}
        aria-invalid={error ? true : undefined}
        {...field}
        value={field.value ?? ""}
      />
      {error ? <p className="text-sm text-danger">{error}</p> : null}
    </div>
  );
};

const ChoiceField = ({
  name,
  label,
  options,
  required = false,
  disabled = false,
  placeholder = "Select"
}: {
  name: keyof UserFormState;
  label: string;
  options: { value: string; label: string }[];
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}) => {
  const [field, , helpers] = useField<string>(name);
  return (
    <div className="flex flex-col gap-1.5">
      <Label>
        {label}
        {required ? <span className="text-danger">*</span> : null}
      </Label>
      <Select
        value={field.value || undefined}
        onValueChange={(v) => helpers.setValue(v)}
        disabled={disabled}
      >
        <SelectTrigger className="w-full" aria-label={label}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

const optionsOf = (values: string[]) =>
  values.map((value) => ({ value, label: humanize(value) }));

function AdminEditUser() {
  const { uid } = useParams<{ uid: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState(true);
  const [showPasswordInput, setShowPasswordInput] = React.useState(false);
  const [initialValues, setInitialValues] = React.useState<UserFormState>({
    email: "",
    firstName: "",
    lastName: "",
    disabled: false,
    newPassword: "",
    role: "",
    practiceName: "",
    visaType: "",
    subscriptionStatus: "",
    lawyerRole: "",
    customerId: ""
  });
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);

  const fetchCustomers = async () => {
    setIsLoadingCustomers(true);
    try {
      const customersRef = collection(db, DATABASE.CUSTOMERS);
      const querySnapshot = await getDocs(customersRef);
      const customersData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        email: doc.data().email,
        practiceName: doc.data().practiceName
      }));
      setCustomers(customersData);
    } catch (error) {
      toast.error("Error fetching customers", {
        description: "Please try again"
      });
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  React.useEffect(() => {
    const fetchUser = async () => {
      setIsLoading(true);
      try {
        const usersData = await getFirebaseUsers("email", uid);
        if (Array.isArray(usersData.users) && usersData.users.length > 0) {
          const userData = usersData.users[0];
          setInitialValues({
            email: userData.email || "",
            firstName: userData.firstName || "",
            lastName: userData.lastName || "",
            disabled: userData.disabled || false,
            newPassword: "",
            role: userData.role,
            practiceName: userData.practiceName || "",
            visaType: userData.visaType || "",
            subscriptionStatus: userData.subscriptionStatus || "",
            lawyerRole: userData.userRole || "",
            customerId: userData.customer_id || ""
          });
        }
      } catch (error) {
        Sentry.captureException(error);
        toast.error("Error fetching user", { description: "Please try again" });
      } finally {
        setIsLoading(false);
      }
    };

    if (uid) fetchUser();
  }, [uid]);

  const viewAsUser = async (targetUserId: string, customRole: string) => {
    try {
      const requestData = await getLoginAsToken(targetUserId);
      if (requestData) {
        navigate("/login", {
          state: { customToken: requestData.token, customRole }
        });
      }
    } catch (error) {
      toast.error("Failed to login as user");
    }
  };

  const column =
    "mx-auto flex w-full max-w-190 flex-col gap-8 px-12 pt-10 pb-24";

  if (isLoading) {
    return (
      <div className="h-full overflow-y-auto bg-surface">
        <div className={column} aria-busy>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  const name = [initialValues.firstName, initialValues.lastName]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="h-full overflow-y-auto bg-surface">
      <div className={column}>
        <PageHeader
          title={name || "Edit User"}
          description={initialValues.email || undefined}
          actions={
            <>
              {initialValues.role === "lawyer" && (
                <Button variant="secondary" size="sm" asChild>
                  <Link
                    to={`/admin/users/new?role=lawyer&practiceName=${initialValues.practiceName}`}
                  >
                    <UserPlus /> New User With Same Access
                  </Link>
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => viewAsUser(uid!, initialValues.role)}
              >
                <Eye /> View Portal as User
              </Button>
            </>
          }
        />

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const changedValues = Object.entries(values).reduce(
                (acc, [key, value]) => {
                  if (value !== initialValues[key as keyof UserFormState]) {
                    acc[key] = value;
                  }
                  return acc;
                },
                {} as Record<string, unknown>
              );

              if (Object.keys(changedValues).length) {
                await updateFirebaseUser(uid!, changedValues);
                toast.success("User updated successfully");
              } else {
                toast.info("No changes to update");
              }
            } catch (error) {
              toast.error("Error updating user", {
                description: "Please try again"
              });
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ handleSubmit, isSubmitting, values, setFieldValue }) => (
            <form onSubmit={handleSubmit} className="flex flex-col gap-8">
              <section className="flex flex-col gap-3">
                <SectionHeader title="Profile" />
                <div className="flex flex-col gap-4 rounded-lg bg-elevated p-4 ring-1 ring-line-2">
                  <TextField name="role" label="Role" readOnly />
                  <div className="flex gap-3 *:flex-1">
                    <TextField name="firstName" label="First Name" required />
                    <TextField name="lastName" label="Last Name" required />
                  </div>
                  <TextField name="email" label="Email" type="email" required />
                  {values.role === "individual" && (
                    <ChoiceField
                      name="visaType"
                      label="Visa Type"
                      options={optionsOf(VISA_TYPES)}
                    />
                  )}
                </div>
              </section>

              {values.role === "lawyer" && (
                <section className="flex flex-col gap-3">
                  <SectionHeader title="Subscription" />
                  <div className="flex flex-col gap-4 rounded-lg bg-elevated p-4 ring-1 ring-line-2">
                    <TextField name="practiceName" label="Practice Name" />
                    <div className="flex gap-3 *:flex-1">
                      <ChoiceField
                        name="subscriptionStatus"
                        label="Subscription Type"
                        options={optionsOf(SUBSCRIPTION_TYPES)}
                      />
                      <ChoiceField
                        name="lawyerRole"
                        label="Account Type"
                        options={optionsOf(LAWYER_ACCOUNT_TYPES)}
                      />
                    </div>
                    {values.subscriptionStatus === "paid" && (
                      <ChoiceField
                        name="customerId"
                        label="Customer"
                        required
                        disabled={isLoadingCustomers}
                        placeholder={
                          isLoadingCustomers ? "Loading…" : "Select Customer"
                        }
                        options={customers.map((c) => ({
                          value: c.id,
                          label: c.practiceName
                        }))}
                      />
                    )}
                  </div>
                </section>
              )}

              <section className="flex flex-col gap-3">
                <SectionHeader title="Access" />
                <div className="flex flex-col gap-4 rounded-lg bg-elevated p-4 ring-1 ring-line-2">
                  <Label className="flex items-center gap-2 text-base text-fg font-normal leading-normal">
                    <Checkbox
                      checked={showPasswordInput}
                      onCheckedChange={(c) => setShowPasswordInput(c === true)}
                    />
                    Manually set the user's password
                  </Label>
                  {showPasswordInput && (
                    <TextField
                      name="newPassword"
                      label="New Password"
                      type="password"
                    />
                  )}
                  <Label className="flex items-center gap-2 text-base text-fg font-normal leading-normal">
                    <Checkbox
                      checked={values.disabled}
                      onCheckedChange={(c) =>
                        setFieldValue("disabled", c === true)
                      }
                    />
                    Disabled
                  </Label>
                </div>
              </section>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate("/admin/users")}
                >
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={isSubmitting}>
                  {isSubmitting ? <Spinner /> : null}
                  Update User
                </Button>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
}

export default AdminEditUser;
